import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import VirtualTryOnModal from './VirtualTryOnModal';
import { ArrowLeft, Loader2, X, MessageCircle, ShoppingBag, CheckCircle, PartyPopper } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PublicVirtualTryOn = () => {
  const { productId } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Estados do checkout/caixa
  const [showCheckout, setShowCheckout] = useState(false);
  const [customerName, setCustomerName] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [selectedSize, setSelectedSize] = useState('');
  const [couponCode, setCouponCode] = useState('');
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [couponError, setCouponError] = useState('');
  const [storeConfig, setStoreConfig] = useState(null);
  
  // NOVO: Estado para modal de sucesso
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [sendingOrder, setSendingOrder] = useState(false);
  const [successCustomerName, setSuccessCustomerName] = useState(''); // Nome para mostrar no modal de sucesso

  useEffect(() => {
    loadProduct();
    fetchStoreConfig();
  }, [productId]);

  const loadProduct = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/products`);
      const products = response.data.products || response.data;
      const foundProduct = products.find(p => p.id === productId);
      
      if (foundProduct) {
        setProduct(foundProduct);
      } else {
        setError('Produto não encontrado');
      }
    } catch (err) {
      console.error('Erro ao carregar produto:', err);
      setError('Erro ao carregar produto');
    } finally {
      setLoading(false);
    }
  };

  const fetchStoreConfig = async () => {
    try {
      const response = await axios.get(`${API}/store-config`);
      setStoreConfig(response.data);
    } catch (err) {
      console.error('Erro ao buscar config da loja:', err);
    }
  };

  const handleGarantirPeca = (data) => {
    console.log('Garantir Peça clicado:', data);
    if (data?.size) {
      setSelectedSize(data.size);
    }
    setShowCheckout(true);
  };

  const applyCoupon = async () => {
    if (!couponCode.trim()) return;
    
    try {
      setCouponError('');
      const response = await axios.post(`${API}/coupons/validate`, {
        code: couponCode.trim().toUpperCase()
      });
      
      if (response.data.valid) {
        setAppliedCoupon(response.data.coupon);
        alert('✅ Cupom aplicado!');
      } else {
        setCouponError('Cupom inválido ou expirado');
      }
    } catch (err) {
      setCouponError('Cupom inválido');
    }
  };

  const removeCoupon = () => {
    setAppliedCoupon(null);
    setCouponCode('');
    setCouponError('');
  };

  const getFinalPrice = () => {
    if (!product?.price) return 0;
    if (!appliedCoupon) return product.price;
    
    const discount = appliedCoupon.discount_type === 'percentage'
      ? product.price * (appliedCoupon.discount_value / 100)
      : appliedCoupon.discount_value;
    
    return Math.max(0, product.price - discount);
  };

  const sendToWhatsApp = async () => {
    if (!customerName.trim() || !customerPhone.trim()) {
      alert('Por favor, preencha seu nome e WhatsApp');
      return;
    }

    setSendingOrder(true);
    
    // Salvar nome ANTES de qualquer operação
    const savedName = customerName.trim();

    try {
      const whatsappNumber = storeConfig?.whatsapp_number || '5555999332159';
      const productName = product?.name || product?.product || 'Produto';
      const price = product?.price || 0;
      const finalPrice = getFinalPrice();
      
      let message = `🛍️ *NOVO PEDIDO - Ver em Mim*\n\n`;
      message += `👤 Cliente: ${savedName}\n`;
      message += `📱 WhatsApp: ${customerPhone}\n\n`;
      message += `🎁 Produto: ${productName}`;
      
      if (selectedSize) {
        message += ` - Tamanho: ${selectedSize}`;
      }
      
      message += `\n💵 Preço: R$ ${price.toFixed(2)}`;
      
      if (appliedCoupon) {
        const discount = price - finalPrice;
        message += `\n💰 Cupom: ${appliedCoupon.code} (-R$ ${discount.toFixed(2)})`;
      }
      
      message += `\n💳 *Total: R$ ${finalPrice.toFixed(2)}*`;
      message += `\n\n✨ Pedido via Virtual Try-On`;

      // Salvar no backend
      try {
        await axios.post(`${API}/virtual-tryon/order`, {
          productId: product?.id || 'unknown',
          productName,
          customerName: savedName,
          customerPhone,
          size: selectedSize || 'Único',
          couponCode: appliedCoupon?.code || null,
          price: finalPrice
        });
        console.log('✅ Pedido salvo com sucesso!');
      } catch (err) {
        console.error('Erro ao salvar pedido:', err);
        // Continua mesmo se der erro no backend
      }

      // Abrir WhatsApp em nova aba
      const whatsappUrl = `https://wa.me/${whatsappNumber}?text=${encodeURIComponent(message)}`;
      window.open(whatsappUrl, '_blank');

      // Salvar nome para exibir no modal de sucesso
      setSuccessCustomerName(savedName);
      
      setSendingOrder(false);
      setShowCheckout(false);
      
      // Mostrar modal de sucesso
      setShowSuccessModal(true);
      
    } catch (error) {
      console.error('Erro ao processar pedido:', error);
      setSendingOrder(false);
      
      // Mesmo com erro, mostra mensagem de sucesso pois o WhatsApp pode ter aberto
      setSuccessCustomerName(savedName);
      setShowCheckout(false);
      setShowSuccessModal(true);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-blue-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-purple-600 mx-auto mb-4" />
          <p className="text-gray-600">Carregando produto...</p>
        </div>
      </div>
    );
  }

  if (error || !product) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-blue-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="text-3xl">😕</span>
          </div>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Produto não encontrado</h2>
          <p className="text-gray-600 mb-6">O produto que você está procurando não existe ou foi removido.</p>
          <button
            onClick={() => navigate('/')}
            className="bg-purple-600 hover:bg-purple-700 text-white px-6 py-3 rounded-lg font-medium transition-colors flex items-center justify-center gap-2 mx-auto"
          >
            <ArrowLeft className="w-4 h-4" />
            Voltar ao Início
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-blue-50">
      {/* Modal Ver em Mim */}
      <VirtualTryOnModal
        isOpen={true}
        onClose={() => navigate('/')}
        product={product}
        onGarantirPeca={handleGarantirPeca}
      />

      {/* Modal de Checkout/Caixa - Otimizado para Mobile */}
      {showCheckout && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end sm:items-center justify-center p-0 sm:p-4 z-[70]">
          <div className="bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl max-w-md w-full max-h-[85vh] sm:max-h-[90vh] overflow-y-auto">
            {/* Header - Compacto para mobile */}
            <div className="sticky top-0 bg-gradient-to-r from-purple-600 to-pink-600 p-3 sm:p-4 rounded-t-2xl z-10">
              <div className="flex justify-between items-center">
                <h3 className="text-lg sm:text-xl font-bold text-white flex items-center gap-2">
                  <ShoppingBag className="w-5 h-5 sm:w-6 sm:h-6" />
                  Garantir Peça Agora
                </h3>
                <button
                  onClick={() => setShowCheckout(false)}
                  className="text-white hover:text-gray-200 transition p-1"
                >
                  <X className="w-5 h-5 sm:w-6 sm:h-6" />
                </button>
              </div>
            </div>

            <div className="p-4 sm:p-6 space-y-3 sm:space-y-4">
              {/* Produto */}
              <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                <h4 className="font-bold text-gray-800 mb-1">{product?.name || product?.product}</h4>
                <p className="text-2xl font-bold text-purple-600">
                  R$ {product?.price?.toFixed(2) || '0.00'}
                </p>
              </div>

              {/* Tamanho */}
              {product?.sizes && product.sizes.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Tamanho
                  </label>
                  <div className="flex gap-2 flex-wrap">
                    {product.sizes.map(size => (
                      <button
                        key={size}
                        onClick={() => setSelectedSize(size)}
                        className={`px-4 py-2 rounded-lg font-bold transition-all ${
                          selectedSize === size
                            ? 'bg-purple-600 text-white'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        {size}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Cupom */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Cupom de Desconto (Opcional)
                </label>
                {!appliedCoupon ? (
                  <div>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={couponCode}
                        onChange={(e) => {
                          setCouponCode(e.target.value.toUpperCase());
                          setCouponError('');
                        }}
                        placeholder="Digite o cupom"
                        className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                      />
                      <button
                        onClick={applyCoupon}
                        disabled={!couponCode.trim()}
                        className="px-6 py-3 bg-purple-600 text-white rounded-lg font-medium hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Aplicar
                      </button>
                    </div>
                    {couponError && (
                      <p className="text-xs text-red-600 mt-1">❌ {couponError}</p>
                    )}
                  </div>
                ) : (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                    <div className="flex justify-between items-center mb-1">
                      <span className="text-green-700 font-bold">✅ {appliedCoupon.code}</span>
                      <button
                        onClick={removeCoupon}
                        className="text-red-600 hover:text-red-800 text-sm font-semibold"
                      >
                        Remover
                      </button>
                    </div>
                    <p className="text-sm text-green-600">
                      Desconto: {appliedCoupon.discount_type === 'percentage' 
                        ? `${appliedCoupon.discount_value}%` 
                        : `R$ ${appliedCoupon.discount_value.toFixed(2)}`}
                    </p>
                  </div>
                )}
              </div>

              {/* Total */}
              {appliedCoupon && (
                <div className="bg-purple-50 p-4 rounded-lg border border-purple-200 space-y-2">
                  <div className="flex justify-between text-sm text-gray-600">
                    <span>Subtotal</span>
                    <span>R$ {product?.price?.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-sm text-green-600">
                    <span>Desconto</span>
                    <span>-R$ {(product.price - getFinalPrice()).toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-lg font-bold text-purple-700 pt-2 border-t border-purple-200">
                    <span>Total Final</span>
                    <span>R$ {getFinalPrice().toFixed(2)}</span>
                  </div>
                </div>
              )}

              {/* Dados do Cliente */}
              <div className="space-y-3">
                <h4 className="font-bold text-gray-700">Seus Dados</h4>
                
                <input
                  type="text"
                  placeholder="Seu nome completo"
                  value={customerName}
                  onChange={(e) => setCustomerName(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                />
                
                <input
                  type="tel"
                  placeholder="Seu WhatsApp (com DDD)"
                  value={customerPhone}
                  onChange={(e) => setCustomerPhone(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                />
              </div>

              {/* Botão Enviar */}
              <button
                onClick={sendToWhatsApp}
                disabled={!customerName.trim() || !customerPhone.trim()}
                className="w-full bg-green-600 hover:bg-green-700 text-white py-4 rounded-xl font-bold text-lg transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {sendingOrder ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Enviando...
                  </>
                ) : (
                  <>
                    <MessageCircle className="w-5 h-5" />
                    Enviar Pedido
                  </>
                )}
              </button>

              {storeConfig?.whatsapp_number && (
                <div className="text-center text-sm text-gray-600">
                  📱 {storeConfig.whatsapp_number.replace(/(\d{2})(\d{2})(\d{4,5})(\d{4})/, '+$1 ($2) $3-$4')}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modal de Sucesso com Mensagem de Natal */}
      {showSuccessModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl shadow-2xl max-w-md w-full overflow-hidden animate-[scaleIn_0.3s_ease-out]">
            {/* Header Natalino */}
            <div className="bg-gradient-to-r from-red-600 via-red-500 to-green-600 p-8 text-center relative overflow-hidden">
              {/* Flocos de neve decorativos */}
              <div className="absolute top-2 left-4 text-white/30 text-2xl">❄️</div>
              <div className="absolute top-4 right-6 text-white/20 text-xl">❄️</div>
              <div className="absolute bottom-4 left-8 text-white/25 text-lg">❄️</div>
              
              <div className="w-20 h-20 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-5xl">🎄</span>
              </div>
              <h2 className="text-2xl font-bold text-white mb-2">
                Pedido Enviado! 🎁
              </h2>
              <p className="text-white/90">
                Seu pedido foi registrado com sucesso
              </p>
            </div>
            
            {/* Corpo com Mensagem de Natal Personalizada */}
            <div className="p-6 text-center">
              {/* Mensagem de Natal Personalizada */}
              <div className="bg-gradient-to-br from-red-50 to-green-50 rounded-2xl p-5 mb-5 border border-red-100">
                <p className="text-2xl mb-2">🎅✨</p>
                <p className="text-gray-800 font-medium text-lg mb-2">
                  Feliz Natal, <span className="text-red-600 font-bold">{successCustomerName.split(' ')[0]}</span>!
                </p>
                <p className="text-gray-600 text-sm">
                  Que esta época mágica traga muita paz, amor e alegria para você e sua família! 
                  Agradecemos por escolher a <span className="font-semibold text-purple-600">Visage de Vogue</span>! 🌟
                </p>
              </div>
              
              {/* Próximos passos - Simplificado */}
              <div className="bg-green-50 rounded-2xl p-4 mb-5">
                <p className="text-green-800 font-medium mb-3">
                  ✅ Próximos passos:
                </p>
                <ul className="text-sm text-green-700 space-y-3">
                  <li className="flex items-center justify-center gap-2">
                    <span className="text-green-500">📞</span>
                    <span>Entraremos em contato em breve</span>
                  </li>
                  <li className="flex items-center justify-center gap-2">
                    <span className="text-green-500">⏳</span>
                    <span>Aguarde nossa confirmação</span>
                  </li>
                </ul>
              </div>
              
              <p className="text-gray-500 text-xs mb-4">
                🎄 Boas Festas e um Próspero Ano Novo! 🎄
              </p>
              
              <button
                onClick={() => {
                  // Tentar fechar a aba/janela para voltar ao Instagram
                  // Se não conseguir fechar (janela principal), redireciona para o Instagram
                  try {
                    window.close();
                    // Se não fechou após 500ms, redireciona
                    setTimeout(() => {
                      window.location.href = 'https://www.instagram.com';
                    }, 500);
                  } catch (e) {
                    window.location.href = 'https://www.instagram.com';
                  }
                }}
                className="w-full bg-gradient-to-r from-red-600 to-green-600 hover:from-red-700 hover:to-green-700 text-white py-4 rounded-xl font-semibold transition-all duration-200 shadow-lg hover:shadow-xl"
              >
                🎁 Fechar e Voltar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PublicVirtualTryOn;
